import React, {FC, useEffect, useRef, useState} from 'react'

type Props = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'defaultValue' | 'onChange' | 'onKeyDown'
> & {
  value: string
  /** Called with the typed text on blur, on Enter, after a pause in typing, and on unmount. */
  onCommit: (value: string) => void
  /** Pause in typing after which the draft is committed anyway. */
  commitDelayMs?: number
  /** Runs after an Enter has committed the draft; `draft` is what was typed. */
  onEnter?: (event: React.KeyboardEvent<HTMLInputElement>, draft: string) => void
}

/**
 * A text input that keeps what is being typed to itself and hands it to the owner only on blur,
 * Enter, a pause in typing, or unmount. For grids where every committed keystroke re-renders
 * (and re-serializes) the whole list: typing a name then costs one row, not every row.
 *
 * A new `value` from the owner replaces the draft only when it differs from what this input last
 * committed, so the echo of its own commit never resets text typed since.
 */
const BufferedTextInput: FC<Props> = ({
  value,
  onCommit,
  commitDelayMs = 400,
  onEnter,
  onBlur,
  ...inputProps
}) => {
  const [draft, setDraft] = useState(value)
  const [committed, setCommitted] = useState(value)
  // Changed from outside (a file import, a row deleted above this one): show it.
  if (value !== committed) {
    setCommitted(value)
    setDraft(value)
  }

  const timerRef = useRef<number | null>(null)
  const draftRef = useRef(draft)
  const committedRef = useRef(committed)
  const onCommitRef = useRef(onCommit)
  useEffect(() => {
    draftRef.current = draft
    committedRef.current = committed
    onCommitRef.current = onCommit
  })

  const commit = (next: string) => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    if (next === committedRef.current) return
    committedRef.current = next
    setCommitted(next)
    onCommitRef.current(next)
  }

  // A draft still waiting when the row leaves the page (paging, deleting) is not lost.
  useEffect(
    () => () => {
      if (timerRef.current === null) return
      window.clearTimeout(timerRef.current)
      if (draftRef.current !== committedRef.current) onCommitRef.current(draftRef.current)
    },
    []
  )

  return (
    <input
      {...inputProps}
      value={draft}
      onChange={(e) => {
        const next = e.target.value
        setDraft(next)
        if (timerRef.current !== null) window.clearTimeout(timerRef.current)
        timerRef.current = window.setTimeout(() => commit(next), commitDelayMs)
      }}
      onBlur={(e) => {
        commit(draft)
        onBlur?.(e)
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return
        commit(draft)
        onEnter?.(e, draft)
      }}
    />
  )
}

export default BufferedTextInput
